import type { Timestamp } from 'firebase/firestore';

// ─── New OPTICS 2026 framework + magic-link review (additive) ────────────────
export * from './optics2026';
export * from './magicLink';

// ─── HR Performance Review Framework (hr_v1) — parallel to OPTICS path ───────
// Source of truth: Performance Review Framework.docx · see CLAUDE.md
export * from './eprFramework';
export * from './calibration';
export * from './appraisal';
export * from './appeal';
export * from './magicLinkToken';

// ─── Workflow ────────────────────────────────────────────────────────────────

export type ReviewWorkflowStatus =
  | 'prep_window'
  | 'self_pending'
  | 'self_submitted'
  | '360_pending'
  | '360_collected'
  | 'manager_pending'
  | 'manager_submitted'
  | 'calibration'
  | 'approved'
  | 'signed_off';

export const WORKFLOW_STEPS: ReviewWorkflowStatus[] = [
  'prep_window', 'self_pending', 'self_submitted', '360_pending',
  '360_collected', 'manager_pending', 'manager_submitted',
  'calibration', 'approved', 'signed_off',
];

export const WORKFLOW_LABELS: Record<ReviewWorkflowStatus, string> = {
  prep_window:       'Prep Window',
  self_pending:      'Self-review Pending',
  self_submitted:    'Self-review Submitted',
  '360_pending':     '360° Pending',
  '360_collected':   '360° Collected',
  manager_pending:   'Manager Review Pending',
  manager_submitted: 'Manager Review Submitted',
  calibration:       'Calibration',
  approved:          'Approved',
  signed_off:        'Signed Off',
};

export type CyclePeriod = 'H1' | 'H2' | 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'annual';
export type CycleStatus = 'draft' | 'active' | 'locked' | 'closed';

// ─── Template ────────────────────────────────────────────────────────────────

export interface OpticsCriteria {
  name: string;
  weight: number; // e.g. 0.10
  questions: string[];
  rubric: Record<number, string>; // { 1: '...', 2: '...', 3: '...', 4: '...', 5: '...' }
}

export interface CharacterCriteria {
  name: string;
  weight: number;
  rubric: Record<number, string>;
}

export interface ReviewTemplate {
  id: string;
  name: string;
  scoringScale: number; // 5
  weightings: {
    okrIndividual: number;  // 0.40
    optics: number;         // 0.30
    character: number;      // 0.30
  };
  teamVsIndividualSplit: {
    individual: number; // 0.60
    team: number;       // 0.40
  };
  opticsCriteria: OpticsCriteria[];
  characterCriteria: CharacterCriteria[];
  maxDiscretionDelta: number; // e.g. 0.5 on 5-pt scale
  createdBy: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── Cycle ───────────────────────────────────────────────────────────────────

export interface ReviewCycle {
  id: string;
  name: string;
  year: number;
  period: CyclePeriod;
  templateId: string;
  status: CycleStatus;
  prepWindowStart: Timestamp;
  startDate: Timestamp;
  lockDate: Timestamp;
  endDate: Timestamp;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;

  // ─── HR Framework (hr_v1) additions ────────────────────────────────────────
  // Optional fields; absence means this cycle runs the OPTICS path only.
  // See CLAUDE.md §A for source-of-truth hierarchy.
  /** Which scoring framework drives this cycle. Default 'optics' for legacy compat. */
  frameworkVersion?: 'optics' | 'hr_v1';
  /** FK to EPRFrameworkTemplate when frameworkVersion === 'hr_v1'. */
  hrFrameworkTemplateId?: string;
  /** Current step in the canonical 7-step sequence (CLAUDE.md §F). UI-facing label. */
  currentStep?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
}

export interface TeamScorecard {
  id: string;
  cycleId: string;
  unitId: string;
  unitName: string;
  score: number;       // 1–5
  setBy: string;
  memberIds: string[];
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── Review Form ─────────────────────────────────────────────────────────────

export interface KRProgressEntry {
  krId: string;
  krTitle: string;
  krNumber?: string;
  selfScore: number;
  evidence: string;
  doneTaskCount?: number;
  // From OKR Tracker keyResults
  progress?: number;      // 0–100
  krStatus?: string;      // e.g. "Achieved", "In Progress", "Not Achieved"
  outcome?: string;
  sourceOfVerification?: string;
  // Quarter / cycle fields (from OKR Tracker)
  quarter?: string;       // legacy single-quarter label e.g. "Q1 2026"
  quarters?: string[];    // multi-quarter e.g. ["Q1 2026", "Q2 2026"]
  quarterlyBreakdown?: Record<string, { progress?: number; targetValue?: number; currentValue?: number; notes?: string }>;
  // Cycle-relevance annotation (set by cycleUtils.filterKRsForCycle)
  isRelevantToCycle?: boolean;
  quarterProgress?: number;   // quarter-specific progress snapshot
  krQuarters?: string[];      // resolved quarter labels for this KR
}

export interface KPIEvidenceEntry {
  tagId: string;
  tagName: string;
  taskCount: number;
  selfScore: number;
}

export interface SelfReviewData {
  krProgress: KRProgressEntry[];
  kpiEvidence: KPIEvidenceEntry[];
  opticsScores: Record<string, number>;    // { criteriaName: aggregateScore }
  opticsQScores: Record<string, number[]>; // { criteriaName: [q0Score, q1Score, …] }
  characterScores: Record<string, number>;
  submittedAt?: Timestamp;
}

export interface ManagerReviewData {
  krScores: { krId: string; managerScore: number; notes: string }[];
  kpiScores: { tagId: string; managerScore: number }[];
  opticsScores: Record<string, number>;
  characterScores: Record<string, number>;
  overallNotes: string;
  submittedAt?: Timestamp;
}

export interface AuditEntry {
  action: string;
  performedBy: string;
  timestamp: Timestamp;
  notes?: string;
}

export interface ReviewForm {
  id: string;
  cycleId: string;
  revieweeId: string;
  revieweeName: string;
  reviewerId: string;
  reviewerName?: string;
  teamScorecardId?: string;
  status: ReviewWorkflowStatus;
  selfReview: Partial<SelfReviewData>;
  responses360Ids: string[];
  /** Token of the self-mint self-invitation. Set on first login-based /self-review visit. */
  selfInvitationToken?: string;
  managerReview: Partial<ManagerReviewData>;
  computedScore?: number;
  calibratedScore?: number;
  discretionAdjustment?: number;
  finalScore?: number;
  auditLog: AuditEntry[];
  approvedBy?: string;
  approvedAt?: Timestamp;
  signedOffBy?: string;
  signedOffAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── 360 ─────────────────────────────────────────────────────────────────────

export interface Review360Invite {
  id: string;
  token: string;
  reviewFormId: string;
  revieweeId: string;
  revieweeName: string;
  cycleId: string;
  responderId?: string;
  status: 'pending' | 'completed' | 'expired';
  expiresAt: Timestamp;
  createdAt: Timestamp;
}

export interface Review360Response {
  id: string;
  inviteId: string;
  reviewFormId: string;
  revieweeId: string;
  cycleId: string;
  responderId: string;
  opticsScores: Record<string, number>;
  createdAt: Timestamp;
}

// ─── Round Table Group ────────────────────────────────────────────────────────

export interface RoundTableGroup {
  id: string;
  cycleId: string;
  name: string;            // e.g. "C-Suite", "Ops Leaders"
  participantIds: string[]; // EPRUser.id of leader participants
  createdBy: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── Round Table Session (Track 2 — Leader Round Table) ──────────────────────

export type RoundTableSessionStatus =
  | 'setup'           // HR configuring
  | 'pre_assessment'  // reviewers + leaders filling async forms
  | 'live'            // live session in progress
  | 'consolidation'   // HR consolidating scores
  | 'completed';      // results published

export interface RoundTableSession {
  id: string;
  name: string;            // e.g. "H1 2026 Leader Review"
  cycleId?: string;        // optional link to an EPR cycle
  sessionDate: string;     // ISO date of live meeting
  locationOrLink: string;  // physical location or video link
  facilitatorId: string;   // EPRUser.id
  facilitatorName: string;
  templateId?: string;     // assessment template (can differ from employee template)
  status: RoundTableSessionStatus;
  preAssessmentDeadline?: string;  // ISO date/time — deadline for async forms
  createdBy: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

/** A leader enrolled in a Round Table session */
export interface RoundTableLeader {
  id: string;
  sessionId: string;
  leaderId: string;
  leaderName: string;
  leaderEmail: string;
  leaderJobTitle?: string;
  unitId?: string;
  unitName?: string;
  // Pulled from OKR Tracker at enrollment time
  krSummary: KRProgressEntry[];
  krDeliveryPct: number;
  // Status flags
  selfAssessmentSubmitted: boolean;
  reviewerCount: number;         // how many reviewers assigned
  reviewsReceived: number;       // how many pre-assessment forms submitted
  // Scoring
  consolidatedScore?: number;
  discretionAdjustment?: number;
  finalScore?: number;
  facilitatorNotes?: string;
  developmentRecommendations?: string;
  createdAt: Timestamp;
}

/** A reviewer assigned to give pre-assessment for a leader in a session */
export interface RoundTableReviewer {
  id: string;
  sessionId: string;
  leaderId: string;
  leaderName: string;
  reviewerId: string;
  reviewerName: string;
  reviewerEmail: string;
  relationship: 'peer_leader' | 'direct_report' | 'cross_function' | 'stakeholder';
  notificationSent: boolean;
  status: 'pending' | 'submitted';
  createdAt: Timestamp;
}

/** Pre-session assessment submitted by a reviewer (async, before live session) */
export interface RoundTableAssessmentResponse {
  id: string;
  sessionId: string;
  leaderId: string;
  reviewerId: string;
  reviewerName: string;
  relationship: RoundTableReviewer['relationship'];
  scores: Record<string, number>;    // criteriaName → score (1–5)
  notes: string;
  strengths: string;
  developmentAreas: string;
  submittedAt: Timestamp;
  createdAt: Timestamp;
}

/** Leader self-assessment for round table */
export interface RoundTableSelfAssessment {
  id: string;
  sessionId: string;
  leaderId: string;
  scores: Record<string, number>;    // criteriaName → score (1–5)
  strategicNotes: string;
  teamGrowthNotes: string;
  crossFunctionNotes: string;
  submittedAt?: Timestamp;
  createdAt: Timestamp;
}

/** Live session note captured during the meeting */
export interface RoundTableLiveNote {
  id: string;
  sessionId: string;
  leaderId: string;
  authorId: string;
  authorName: string;
  content: string;
  liveScore?: number;  // optional real-time score update
  criteriaName?: string;
  createdAt: Timestamp;
}

/** Consolidated result for a leader (produced by HR post-session) */
export interface RoundTableResult {
  id: string;
  sessionId: string;
  leaderId: string;
  leaderName: string;
  preAssessmentAvg: Record<string, number>;   // criteriaName → avg across all reviewer responses
  selfAssessmentScores: Record<string, number>;
  liveSessionScore?: number;
  consolidatedScore: number;    // weighted blend
  discretionAdjustment: number; // bounded delta (reason required)
  discretionReason: string;
  finalScore: number;
  facilitatorNotes: string;
  developmentRecommendations: string;
  publishedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── Discretion Log (immutable) ───────────────────────────────────────────────

export interface EPDiscretionLog {
  id: string;
  reviewFormId: string;
  revieweeId: string;
  adjustedBy: string;
  previousScore: number;
  newScore: number;
  delta: number;
  reason: string;
  timestamp: Timestamp;
}

// ─── Auth user shape ─────────────────────────────────────────────────────────

export type EPRUserRole = 'superadmin' | 'admin' | 'hr_admin' | 'leader' | 'employee';

export interface EPRUser {
  id: string;
  uid: string;
  name: string;
  email: string;
  role: EPRUserRole;
  unitId?: string;
  directManagerId?: string;
  jobTitle?: string;
  grade?: string;          // HRIS grade e.g. 'G1'–'G7'
  appRoles?: {
    okr?:    'viewer' | 'editor' | 'admin' | 'superadmin';
    epr?:    EPRUserRole;
    portal?: 'viewer' | 'admin' | 'superadmin';
  };
  isLeader: boolean;
  isActive: boolean;
  appAccess: string[];
}

// ─── Computed scores (local only, not stored) ─────────────────────────────────

export interface ComputedFormScores {
  individualOkrScore: number;
  teamScore: number;
  okrIndividualScore: number;
  opticsScore: number;
  characterScore: number;
  totalScore: number;
}

// ─── Default criteria for new templates ──────────────────────────────────────

export const DEFAULT_OPTICS_CRITERIA: OpticsCriteria[] = [
  {
    name: 'Ownership',
    weight: 0.10,
    questions: [
      'I take full responsibility for my deliverables.',
      'I proactively identify and resolve issues without being asked.',
      'I consistently follow through on commitments and deadlines.',
      'I hold myself accountable for quality outcomes.',
    ],
    rubric: {
      1: 'Rarely takes ownership; avoids accountability.',
      2: 'Takes ownership occasionally but inconsistently.',
      3: 'Generally owns their work and outcomes.',
      4: 'Consistently owns results, including challenges.',
      5: 'Role model for ownership; holds the team accountable too.',
    },
  },
  {
    name: 'Transparency',
    weight: 0.10,
    questions: [
      'I communicate progress and setbacks openly and in a timely manner.',
      'I share relevant information proactively with my team.',
      'I am honest about my capacity and limitations.',
      'I raise issues early rather than letting them escalate.',
    ],
    rubric: {
      1: 'Often withholds information; creates silos.',
      2: 'Shares information reactively when prompted.',
      3: 'Communicates adequately; some proactive sharing.',
      4: 'Consistently open and timely in communications.',
      5: 'Exceptional transparency; role model for information flow.',
    },
  },
  {
    name: 'Positive Impact',
    weight: 0.025,
    questions: [
      'My work contributes meaningfully to team and company goals.',
      'I create positive energy and momentum in the team.',
      'I look for ways to improve processes beyond the minimum required.',
    ],
    rubric: {
      1: 'Minimal impact; often neutral or negative energy.',
      2: 'Some positive contribution but inconsistent.',
      3: 'Reliably positive contributor.',
      4: 'Drives positive outcomes beyond own scope.',
      5: 'Transformative positive impact across team/org.',
    },
  },
  {
    name: 'Integrity',
    weight: 0.025,
    questions: [
      'I act in alignment with company values even when no one is watching.',
      'I am honest and ethical in all my interactions.',
      'I treat all team members with fairness and respect.',
    ],
    rubric: {
      1: 'Integrity issues noted; unethical behaviour observed.',
      2: 'Generally ethical but with occasional lapses.',
      3: 'Consistent integrity in day-to-day work.',
      4: 'Strong ethical compass; earns high trust from peers.',
      5: 'Exemplary integrity; trusted guardian of company values.',
    },
  },
  {
    name: 'Collaboration',
    weight: 0.025,
    questions: [
      'I actively support team members in achieving shared goals.',
      'I communicate effectively across teams and functions.',
      'I provide helpful feedback and receive it constructively.',
    ],
    rubric: {
      1: 'Works in isolation; poor cross-team cooperation.',
      2: 'Collaborates when required but not proactively.',
      3: 'Good team player; generally collaborative.',
      4: 'Actively builds bridges; strong cross-functional partnership.',
      5: 'Exceptional collaborator; multiplies team effectiveness.',
    },
  },
  {
    name: 'Solutions',
    weight: 0.025,
    questions: [
      'I bring actionable solutions rather than just problems.',
      'I think creatively to overcome obstacles.',
      'I adapt quickly and constructively when situations change.',
    ],
    rubric: {
      1: 'Primarily highlights problems without offering solutions.',
      2: 'Occasionally suggests solutions when prompted.',
      3: 'Usually brings actionable options to discussions.',
      4: 'Consistently solution-focused with high adaptability.',
      5: 'Exceptional problem-solver; solutions-first mindset.',
    },
  },
];

export const DEFAULT_CHARACTER_CRITERIA: CharacterCriteria[] = [
  {
    name: 'Leadership Presence',
    weight: 0.15,
    rubric: {
      1: 'No leadership presence; passive in team settings.',
      2: 'Minimal leadership; speaks up occasionally.',
      3: 'Adequate leadership within own scope.',
      4: 'Strong presence; consistently influences others positively.',
      5: 'Inspiring leader; elevates everyone around them.',
    },
  },
  {
    name: 'Growth Mindset',
    weight: 0.15,
    rubric: {
      1: 'Resistant to feedback and change.',
      2: 'Accepts feedback but slow to apply it.',
      3: 'Open to growth; applies feedback moderately.',
      4: 'Actively seeks development; fast learner.',
      5: "Exceptional learner; drives own and team's growth.",
    },
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// LEADER REVIEW MODULE — Unified Leader EPR + Round Table
// ═══════════════════════════════════════════════════════════════════════════════

export type LeaderReviewStatus =
  | 'setup'           // Admin configuring session
  | 'self_review'     // Leaders completing self-assessment
  | 'peer_review'     // Leaders rating peers
  | 'live'            // Live group session
  | 'consolidation'   // Admin consolidating scores
  | 'completed';      // Results published — visible to all leaders

export const LEADER_REVIEW_STATUS_LABELS: Record<LeaderReviewStatus, string> = {
  setup:         'Setup',
  self_review:   'Self-Review Open',
  peer_review:   'Peer Review Open',
  live:          'Live Session',
  consolidation: 'Consolidation',
  completed:     'Results Published',
};

export const LEADER_REVIEW_STAGES: LeaderReviewStatus[] = [
  'setup', 'self_review', 'peer_review', 'live', 'consolidation', 'completed',
];

/** Modular score formula — weights must sum to 1.0 */
export interface LeaderReviewScoreWeights {
  peer: number;  // e.g. 0.50
  self: number;  // e.g. 0.30
  live: number;  // e.g. 0.20
}

export interface LeaderReviewSession {
  id: string;
  name: string;                     // "H1 2026 Leader EPR"
  period: CyclePeriod;              // H1 | H2
  year: number;
  status: LeaderReviewStatus;
  scoreWeights: LeaderReviewScoreWeights;
  selfReviewStartDate?: string;     // ISO date — auto-opens self-review from setup
  selfReviewDeadline: string;       // ISO date string — auto-advances to peer_review if passed
  peerReviewDeadline: string;       // ISO date — auto-advances to live if passed
  liveSessionDate: string;
  facilitatorId?: string;           // uid of leader running live session (defaults to createdBy)
  facilitatorName?: string;
  templateId?: string;              // leaderReviewTemplates doc id; null = use default LEADER_MUSCLES
  createdBy: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

export interface LeaderReviewEnrollment {
  id: string;
  sessionId: string;
  leaderId: string;
  leaderName: string;
  leaderEmail: string;
  leaderJobTitle?: string;
  krSummary: KRProgressEntry[];
  krDeliveryPct: number;
  selfSubmitted: boolean;
  peerSubmittedCount: number;
  totalPeerCount: number;
  finalScore?: number;
  published: boolean;
  createdAt: Timestamp;
}

export interface LeaderMuscleScore {
  muscleKey: string;
  score: number;      // 1–5
  example?: string;
  impactTags?: string[];   // quick-click qualitative labels
}

/** Per-KR reflection written during self-assessment OKR step */
export interface KRReflection {
  krId: string;
  krTitle: string;
  krNumber?: string;
  status: string;        // e.g. "Achieved", "In Progress", "Not Achieved"
  progress: number;      // 0–100
  reflection: string;    // free-text reflection from leader
}

export interface LeaderReviewSelfAssessment {
  id: string;
  sessionId: string;
  leaderId: string;
  scores: LeaderMuscleScore[];
  krReflections?: KRReflection[];   // OKR anchor reflections
  submittedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

export interface LeaderReviewPeerRating {
  id: string;
  sessionId: string;
  raterId: string;
  ratedLeaderId: string;
  scores: LeaderMuscleScore[];
  strength?: string;
  growthArea?: string;
  submittedAt: Timestamp;
  createdAt: Timestamp;
}

export interface LeaderReviewLiveNote {
  id: string;
  sessionId: string;
  leaderId: string;
  authorId: string;
  content: string;
  liveScore?: number;
  createdAt: Timestamp;
}

export interface LeaderReviewResult {
  id: string;
  sessionId: string;
  leaderId: string;
  leaderName: string;
  krDeliveryPct: number;
  selfScores: LeaderMuscleScore[];
  peerAvgScores: LeaderMuscleScore[];
  liveScore?: number;
  consolidatedScore: number;
  discretionAdjustment: number;
  discretionReason: string;
  finalScore: number;
  facilitatorNotes: string;
  developmentRecommendations: string;
  publishedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── 8 Leadership Muscles ─────────────────────────────────────────────────────

export interface LeaderMuscle {
  key: string;
  name: string;
  shortDesc: string;
  question: string;
  goodLooksLike: string;   // observable strong behaviour anchor
  weakLooksLike: string;   // observable weak behaviour anchor
  rubric: Record<number, string>;
}

// ─── Template system ──────────────────────────────────────────────────────────

export interface LeaderReviewTemplate {
  id: string;
  name: string;
  description?: string;
  muscles: LeaderMuscle[];
  isDefault: boolean;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── Default 8 muscles — H1 2026 Leadership Manifesto framework ──────────────
// Scoring anchored to observable behaviour & impact (not confidence/style/visibility)

export const LEADER_MUSCLES: LeaderMuscle[] = [
  {
    key: 'pull_the_train',
    name: 'Pull the Train',
    shortDesc: 'Ownership & follow-through',
    question: 'Does this leader take ownership and drive work forward without needing to be chased?',
    goodLooksLike: 'Takes ownership without being asked. Drives deliverables to completion. Holds the team accountable to commitments. Never needs chasing.',
    weakLooksLike: 'Waits for direction before acting. Drops balls regularly. Points to obstacles instead of solving them. Creates follow-up burden for others.',
    rubric: {
      1: 'Rarely follows through; needs constant follow-up',
      2: 'Inconsistent — sometimes drops the ball',
      3: 'Generally reliable; follows through on most commitments',
      4: 'Consistently drives work forward independently',
      5: 'Pulls the whole team forward; never needs chasing',
    },
  },
  {
    key: 'level_up',
    name: 'Level Up',
    shortDesc: 'Growth & learning',
    question: 'Is this leader visibly improving, applying feedback, and growing their role maturity?',
    goodLooksLike: 'Actively seeks feedback and applies it visibly. Shows clear growth quarter on quarter. Elevates others through their own learning.',
    weakLooksLike: 'Repeats the same mistakes. Deflects or ignores feedback. Shows little observable growth in how they lead or decide.',
    rubric: {
      1: 'Resistant to growth; repeats the same mistakes',
      2: 'Some learning but inconsistent application',
      3: 'Shows steady improvement over time',
      4: 'Actively seeks feedback and applies it quickly',
      5: 'Exceptional growth mindset; elevates others too',
    },
  },
  {
    key: 'raise_the_floor',
    name: 'Raise the Floor',
    shortDesc: 'Standards & consistency',
    question: 'Does this leader protect quality standards and lift the team\'s baseline consistently?',
    goodLooksLike: 'Protects quality standards consistently. Calls out substandard work constructively. The team\'s baseline rises because of their presence.',
    weakLooksLike: 'Lets standards slip. Inconsistent quality across their work. Doesn\'t notice or address low-quality outputs from the team.',
    rubric: {
      1: 'Standards slip regularly; quality is a concern',
      2: 'Inconsistent — quality varies significantly',
      3: 'Meets standards consistently',
      4: 'Actively raises the bar; quality-conscious',
      5: 'Sets the standard for the whole team',
    },
  },
  {
    key: 'lead_the_edges',
    name: 'Lead the Edges',
    shortDesc: 'Risk awareness & early action',
    question: 'Does this leader catch risks and gaps early, and act before issues escalate?',
    goodLooksLike: 'Spots risks and edge cases early. Flags them with context and a suggested action. Prevents small issues from becoming big problems.',
    weakLooksLike: 'Only notices problems when they become crises. Consistently caught off-guard. Reacts rather than prevents.',
    rubric: {
      1: 'Misses issues until they escalate',
      2: 'Occasionally spots problems early',
      3: 'Usually identifies issues in good time',
      4: 'Consistently catches risks early and acts',
      5: 'Highly attuned; prevents issues before they surface',
    },
  },
  {
    key: 'think_in_horizons',
    name: 'Think in Horizons',
    shortDesc: 'Strategic thinking',
    question: 'Does this leader connect daily decisions to long-term direction and wider impact?',
    goodLooksLike: 'Connects daily work to quarterly OKRs and company direction. Makes decisions that hold up over time. Balances short and long-term trade-offs.',
    weakLooksLike: 'Solves only the immediate problem. Decisions create downstream friction. Rarely thinks past the current week or task.',
    rubric: {
      1: 'Focused only on the immediate task',
      2: 'Some broader awareness but mostly tactical',
      3: 'Connects own work to team and company goals',
      4: 'Regularly thinks across quarters and teams',
      5: 'Strategic thinker; always aware of the wider landscape',
    },
  },
  {
    key: 'adaptability_quotient',
    name: 'Adaptability Quotient (AQ)',
    shortDesc: 'Adjusting to change',
    question: 'How well does this leader adjust to change, pressure, and shifting priorities?',
    goodLooksLike: 'Pivots quickly when direction changes. Maintains composure under pressure. Helps the team navigate ambiguity with confidence.',
    weakLooksLike: 'Struggles visibly with change. Slows team momentum during transitions. Becomes rigid or disengaged under pressure.',
    rubric: {
      1: 'Struggles significantly with change',
      2: 'Adapts slowly or reluctantly',
      3: 'Adapts adequately when change is needed',
      4: 'Adjusts quickly and constructively',
      5: 'Thrives under change; actively helps others adapt',
    },
  },
  {
    key: 'operational_intelligence',
    name: 'Operational Intelligence',
    shortDesc: 'Practical judgment',
    question: 'Does this leader show sound judgment and understand how work flows across the system?',
    goodLooksLike: 'Understands how work flows across the organisation. Makes sound judgment calls under pressure. Designs practical, scalable solutions.',
    weakLooksLike: 'Creates process friction. Poor prioritisation judgment. Doesn\'t understand how their decisions affect other parts of the system.',
    rubric: {
      1: 'Poor practical judgment; creates process friction',
      2: 'Basic operational awareness only',
      3: 'Sound operational understanding in most situations',
      4: 'Strong practical judgment across contexts',
      5: 'Exceptional operational mastery; improves how things work',
    },
  },
  {
    key: 'pre_sense',
    name: 'Pre-Sense',
    shortDesc: 'Anticipation & pattern recognition',
    question: 'Does this leader anticipate needs, read patterns, and act before problems arrive?',
    goodLooksLike: 'Anticipates team and customer needs before they surface. Reads patterns and prepares proactively. Consistently one step ahead.',
    weakLooksLike: 'Purely reactive. Consistently surprised by predictable problems. Acts only when issues arrive — never before.',
    rubric: {
      1: 'Purely reactive; rarely anticipates',
      2: 'Occasionally thinks ahead',
      3: 'Usually anticipates key needs before they arise',
      4: 'Regularly spots patterns and prepares',
      5: 'Exceptional foresight; acts on signals others miss',
    },
  },
];
